import { items } from "@/api/items/useItemQuery";
import { KPI } from "@/api/kpi/useKPIQuery";
import { columnlist } from "@/constants/columnlist";
import { useModal } from "@/features/modal/ModalStackManager";
import { Header } from "@/features/standard/Header";
import { HeaderSubTitle } from "@/features/standard/SubTitle";
import { HeaderTitle } from "@/features/standard/Title";
import { ItemAutoComplete } from "@/features/ui/autoComplete/item/item-autoComplete";
import { Calendar, isCalendarDate } from "@/features/ui/Calendar";
import { GridTable } from "@/features/ui/PMS/GridTable";
import CustomTable from "@/features/ui/table/CustomTable";
import { DatetimeUtilMinute } from "@/utils/dateTimeUtil";
import timeUtil from "@/utils/timeUtil";
import { ActionIcon, Flex, Space, Text } from "@mantine/core";
import { ItemsGet200ResponseRowsInnerItemTypeEnum } from "@sizlcorp/sizl-api-document/dist/models";
import { IconCalendar } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useEffect, useState } from "react";

const KPIPowerUsage = Object.assign({}, {
    Header: Header,
    HeaderTitle: HeaderTitle,
    HeaderSubTitle: HeaderSubTitle,
    CustomTable: CustomTable,
})

export const KPIPowerUsagePage = () => {
    const today = dayjs().subtract(1, 'day').startOf('day').toDate();
    const oneWeekAgo = dayjs().subtract(7, 'day').startOf('day').toDate();

    const [content, setContent] = useState<any>([]);
    const [selectList, setSelectList] = useState<Set<number>>(new Set());
    const [stateDate, setStateDate] = useState<[Date | null, Date | null]>([oneWeekAgo, today]);
    const [item, setItem] = useState<string | null>('');
    const [averagePowerUsage, setAveragePowerUsage] = useState<string>('');
    const { openModal } = useModal();

    const { data } = useQuery({
        ...items.get({
            query: {
                $and: [
                    { code: item }
                ]
            }
        }),
        enabled: !!item
    })

    const itemData = data?.data?.rows && data?.data?.rows[0]
    const TableHeader = {
        machine: ['품목코드', '품목명', '규격', '개당 평균 전력 사용량(kWh)'],
    }

    const BodyData = {
        machine: [{ '품목코드': itemData?.code ?? '-', '품목명': itemData?.name, '규격': itemData?.spec, '개당 평균 전력 사용량(kWh)': averagePowerUsage === "NaN" ? '-' : averagePowerUsage }],
    }

    const { data: powerUsageData } = useQuery({
        ...KPI.powerUsage({
            itemCode: item ?? '',
            scheduledAtFrom: stateDate[0]?.toISOString() ?? '',
            scheduledAtTo: stateDate[1]?.toISOString() ?? ''
        }),
        enabled: !!item
    })

    useEffect(() => {
        if (powerUsageData?.data) {
            const transformedData = powerUsageData.data.map((powerUsage: any) => ({
                id: powerUsage.id,
                productionPlanId: powerUsage.productionPlanId,
                operationName: powerUsage.operationName,
                equipmentName: powerUsage.equipmentName,
                equipmentVolt: powerUsage.equipmentVolt,
                itemCode: powerUsage.itemCode,
                itemName: powerUsage.itemName,
                itemSpec: powerUsage.itemSpec,
                workerName: powerUsage.workerName,
                workStartTime: DatetimeUtilMinute(powerUsage.workStartTime),
                workEndTime: DatetimeUtilMinute(powerUsage.workEndTime),
                pauseDuration: powerUsage.pauseDuration,
                totalQuantity: `${powerUsage.totalQuantity} ${itemData?.unitText}`,
                goodQuantity: `${powerUsage.goodQuantity} ${itemData?.unitText}`,
                defectQuantity: `${powerUsage.defectQuantity} ${itemData?.unitText}`,
                electricPower: powerUsage.electricPower,
            }));

            const totalElectricPower = powerUsageData?.data?.reduce((sum: number, powerUsage: any) => sum + parseFloat(powerUsage.electricPower), 0) ?? 0;
            const averageElectricPower = (totalElectricPower / powerUsageData.data.length).toFixed(2);
            setAveragePowerUsage(averageElectricPower);
            setContent(transformedData);
        } else {
            setContent([]);
        }
    }, [powerUsageData]);

    return (
        <Flex gap="md" direction={'column'}>
            <KPIPowerUsage.Header>
                <KPIPowerUsage.HeaderTitle>전력 사용량(E)</KPIPowerUsage.HeaderTitle>
                <KPIPowerUsage.HeaderSubTitle>작업지시 당 소모한 전력량</KPIPowerUsage.HeaderSubTitle>
            </KPIPowerUsage.Header>
            <Flex justify={'space-between'} align={'center'}>
                <Space miw={'22rem'} />
                <Flex align={'center'}>
                    <Flex align="center" gap="sm" onClick={() => {
                        openModal(<Calendar dates={stateDate} type="range" isExclude={true} />, null, '날짜 선택')
                            .then((value) => {
                                if (isCalendarDate(value)) {
                                    setStateDate(value);
                                }
                            });
                    }}>
                        <Text size="2rem" fw="bold" style={{ cursor: "pointer" }}>
                            {timeUtil(stateDate[0]?.toISOString() ?? '')} ~ {timeUtil(stateDate[1]?.toISOString() ?? '')}
                        </Text>
                        <ActionIcon>
                            <IconCalendar size="2rem" />
                        </ActionIcon>
                    </Flex>
                </Flex>
                <ItemAutoComplete
                    placeholder="품목을 선택해주세요"
                    w="22rem"
                    size="lg"
                    getItemType={[ItemsGet200ResponseRowsInnerItemTypeEnum.PRODUCT, ItemsGet200ResponseRowsInnerItemTypeEnum.SEMI_PRODUCT]}
                    onChange={(value) => setItem(value)}
                />
            </Flex>

            <KPIPowerUsage.CustomTable
                headers={TableHeader.machine}
                data={BodyData.machine}
            />
            <GridTable
                headerList={[
                    ...columnlist.powerUsageTable
                ]}
                row={content}
                setRow={setContent}
                selectList={selectList}
                //@ts-ignore
                setSelectList={setSelectList}
                width={'100%'}
                height={750}
                resizable={true}
            // scrollEnd={(value) => {
            //     if(value){
            //         if(keyword !== undefined){
            //             if(searchPage.total_page > searchPage.current_page){
            //                 getPressDailyStatusSearchApi(keyword, searchPage.current_page+ 1)
            //             }
            //         }else{
            //             if(page.total_page > page.current_page){
            //                 getPressDailyStatusApi(page.current_page+ 1)
            //             }
            //         }
            //     }
            // }}
            />
        </Flex>
    )
}